import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Stack,
  Divider,
  useTheme,
  Paper,
  Chip,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material'
import {
  KingBed as KingBedIcon,
  Wifi as WifiIcon,
  LocalParking as ParkingIcon,
  Pool as PoolIcon,
  Room as RoomIcon,
  RoomService as RoomServiceIcon,
  LocalBar as BarIcon,
  Stars as StarsIcon,
  ChildCare as ChildCareIcon,
  Tv as TvIcon,
  AcUnit as AcIcon,
  Coffee as CoffeeIcon,
  Restaurant as RestaurantIcon,
  Iron as IronIcon,
  LocalLaundryService as LaundryIcon,
  Bathtub as BathtubIcon,
  Kitchen as KitchenIcon,
  Balcony as BalconyIcon,
  Desk as DeskIcon,
  PhoneEnabled as PhoneIcon,
  Fireplace as FireplaceIcon,
  Spa as SpaIcon,
  Person as PersonIcon,
  Square as SquareIcon,
} from '@mui/icons-material'
import { useCurrency } from '../context/CurrencyContext'
import { fetchRooms, type Room } from '../services/roomService'
import BookingContactModal from './BookingContactModal'

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

// Export amenityIcons for use in SearchResults component
export const amenityIcons: { [key: string]: JSX.Element } = {
  'AC': <AcIcon />,
  'TV': <TvIcon />,
  'WiFi': <WifiIcon />,
  'Mini Bar': <BarIcon />,
  'Room Service': <RoomServiceIcon />,
  'Coffee Maker': <CoffeeIcon />,
  'Iron & Board': <IronIcon />,
  'Hair Dryer': <RoomServiceIcon />,
  'Safe': <RoomServiceIcon />,
  'Desk': <DeskIcon />,
  'Bathtub': <BathtubIcon />,
  'Shower': <BathtubIcon />,
  'Toiletries': <RoomServiceIcon />,
  'Towels': <RoomServiceIcon />,
  'Wardrobe': <RoomServiceIcon />,
  'Telephone': <PhoneIcon />,
  'Refrigerator': <KitchenIcon />,
  'Microwave': <KitchenIcon />,
}

const MotionTypography = motion(Typography)

const roomTypes = [
  'Deluxe',
  'Super Deluxe',
  'Family Suite',
  'Deluxe Non-AC',
];

const Rooms = () => {
  const { convertPrice } = useCurrency()
  const navigate = useNavigate()
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })
  const theme = useTheme()
  const [rooms, setRooms] = useState<Room[]>([])
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null)
  const [contactModalRoom, setContactModalRoom] = useState<Room | null>(null)
  const [searchParams, setSearchParams] = useState({
    checkIn: '',
    checkOut: '',
    adults: 1,
    children: 0,
    roomType: 'all'
  })

  useEffect(() => {
    fetchRooms().then(setRooms)
  }, [])

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    
    // Create query string from search params
    const queryParams = new URLSearchParams({
      checkIn: searchParams.checkIn,
      checkOut: searchParams.checkOut,
      adults: searchParams.adults.toString(),
      children: searchParams.children.toString(),
      roomType: searchParams.roomType
    });

    // Navigate to results page with search params
    navigate(`/rooms/search?${queryParams.toString()}`);
  };

  const handleBookNow = (room: Room) => {
    setContactModalRoom(room)
  }

  return (
    <Box>
      <BookingContactModal
        open={contactModalRoom !== null}
        onClose={() => setContactModalRoom(null)}
        roomName={contactModalRoom?.name}
      />
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 4, md: 8 },
        }}
      >
        <Container 
          maxWidth="xl" 
          sx={{ 
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Grid 
            container 
            spacing={{ xs: 3, md: 8 }} 
            alignItems="center" 
          >
            {/* Search Form - Moved to top on mobile */}
            <Grid 
              item 
              xs={12} 
              md={6}
              sx={{
                order: { xs: 1, md: 2 },
                mb: { xs: 3, md: 0 },
              }}
            >
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                sx={{
                  width: '100%',
                }}
              >
                <Paper
                  elevation={2}
                        sx={{
                    p: { xs: 2.5, sm: 3, md: 4 },
                    width: '100%',
                    borderRadius: { xs: 2, md: 3 },
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <form onSubmit={handleSearch}>
                    <Stack spacing={{ xs: 2, md: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          textAlign: 'center',
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                          mb: { xs: 1, md: 2 },
                        }}
                      >
                        Search Available Rooms
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 600,
                              fontSize: { xs: '0.8rem', md: '0.875rem' },
                            }}
                          >
                            Check-in Date
                          </Typography>
                          <input
                            type="date"
                            value={searchParams.checkIn}
                            onChange={(e) => setSearchParams(prev => ({ ...prev, checkIn: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              outline: 'none',
                              backgroundColor: '#fff',
                            }}
                            min={new Date().toISOString().split('T')[0]}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 600,
                              fontSize: { xs: '0.8rem', md: '0.875rem' },
                            }}
                          >
                            Check-out Date
                          </Typography>
                          <input
                            type="date"
                            value={searchParams.checkOut}
                            onChange={(e) => setSearchParams(prev => ({ ...prev, checkOut: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              outline: 'none',
                              backgroundColor: '#fff',
                            }}
                            min={searchParams.checkIn || new Date().toISOString().split('T')[0]}
                            required
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 600,
                              fontSize: { xs: '0.8rem', md: '0.875rem' },
                            }}
                          >
                            Adults
                          </Typography>
                          <input
                            type="number"
                            value={searchParams.adults}
                            onChange={(e) => setSearchParams(prev => ({ ...prev, adults: parseInt(e.target.value) }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              outline: 'none',
                              backgroundColor: '#fff',
                              WebkitAppearance: 'none',
                              MozAppearance: 'textfield',
                            }}
                            min="1"
                            max="6"
                            required
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 600,
                              fontSize: { xs: '0.8rem', md: '0.875rem' },
                            }}
                          >
                            Children
                          </Typography>
                          <input
                            type="number"
                            value={searchParams.children}
                            onChange={(e) => setSearchParams(prev => ({ ...prev, children: parseInt(e.target.value) }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              outline: 'none',
                              backgroundColor: '#fff',
                              WebkitAppearance: 'none',
                              MozAppearance: 'textfield',
                            }}
                            min="0"
                            max="4"
                          />
                        </Grid>
                      </Grid>

                      <Box>
                        <Typography 
                          variant="subtitle2" 
                          sx={{
                            mb: 1, 
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                          }}
                        >
                          Room Type
                        </Typography>
                        <select
                          value={searchParams.roomType}
                          onChange={(e) => setSearchParams(prev => ({ ...prev, roomType: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid rgba(0,0,0,0.1)',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            backgroundColor: '#fff',
                            outline: 'none',
                          }}
                        >
                          <option value="all">All Types</option>
                          {roomTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </Box>

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                          py: { xs: 1.5, md: 1.8 },
                          mt: { xs: 1, md: 2 },
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s',
                          },
                        }}
                      >
                        Search Available Rooms
                      </Button>
                    </Stack>
                  </form>
                </Paper>
                    </Box>
                  </Grid>

            {/* Content Section - Moved to bottom on mobile */}
            <Grid 
              item 
              xs={12} 
              md={6} 
              sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                justifyContent: 'center',
                order: { xs: 2, md: 1 },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              <Box>
                <MotionTypography
                  variant="overline"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    letterSpacing: 2,
                    mb: { xs: 1, md: 2 },
                    display: 'block',
                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                  }}
                >
                  ACCOMMODATIONS
                </MotionTypography>
                
                <MotionTypography
                  variant="h2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                          sx={{
                            fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    mb: { xs: 2, md: 3 },
                    background: theme => `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Find Your Perfect Stay
                </MotionTypography>

                <MotionTypography
                  variant="body1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  sx={{
                    color: 'text.secondary',
                    mb: { xs: 3, md: 4 },
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    maxWidth: { xs: '100%', md: '500px' },
                    mx: { xs: 'auto', md: 0 },
                    lineHeight: 1.8,
                  }}
                >
                  Discover our collection of thoughtfully designed rooms and suites, 
                  each offering a unique blend of comfort, elegance, and modern amenities.
                </MotionTypography>

                <Stack
                  direction="row"
                  spacing={{ xs: 2, sm: 3 }}
                  justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
                  divider={<Divider orientation="vertical" flexItem />}
                  sx={{ 
                    mb: { xs: 0, md: 4 },
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    gap: { xs: 3, md: 0 },
                  }}
                >
                  <Box sx={{ 
                    textAlign: { xs: 'left', md: 'left' }, 
                    minWidth: { xs: '100px', sm: '120px' },
                    flex: { xs: '0 0 auto', md: 1 },
                  }}>
                    <Typography 
                      variant="h4" 
                      color="primary.main" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 0.5,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      }}
                    >
                      24/7
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 500,
                          }}
                        >
                      Room Service
                        </Typography>
                      </Box>
                          <Box sx={{
                    textAlign: { xs: 'left', md: 'left' }, 
                    minWidth: { xs: '100px', sm: '120px' },
                    flex: { xs: '0 0 auto', md: 1 },
                          }}>
                            <Typography 
                      variant="h4" 
                      color="primary.main" 
                              sx={{ 
                                fontWeight: 700,
                        mb: 0.5,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                              }}
                            >
                      100%
                    </Typography>
                              <Typography
                      variant="body2" 
                      color="text.secondary"
                                sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 500,
                                }}
                              >
                      Satisfaction
                              </Typography>
                  </Box>
                  <Box sx={{ 
                    textAlign: { xs: 'left', md: 'left' }, 
                    minWidth: { xs: '100px', sm: '120px' },
                    flex: { xs: '0 0 auto', md: 1 },
                  }}>
                    <Typography 
                      variant="h4" 
                      color="primary.main" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 0.5,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      }}
                    >
                      4.8
                            </Typography>
                            <Typography
                      variant="body2" 
                      color="text.secondary"
                              sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                fontWeight: 500,
                              }}
                            >
                      Guest Rating
                            </Typography>
                          </Box>
                </Stack>

                {/* Feature Highlights */}
                <Stack
                  direction="row"
                  spacing={2}
                          sx={{
                    mt: { xs: 4, md: 0 },
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: { xs: 'flex-start', md: 'flex-start' },
                  }}
                >
                            <Chip
                    icon={<WifiIcon />}
                    label="Free High-Speed WiFi"
                              variant="outlined"
                              sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                                '& .MuiChip-icon': {
                                  color: 'primary.main',
                                },
                                  px: 1,
                    }}
                  />
                  <Chip
                    icon={<ParkingIcon />}
                    label="Free Parking"
                    variant="outlined"
                          sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '& .MuiChip-icon': {
                        color: 'primary.main',
                      },
                      px: 1,
                    }}
                  />
                  <Chip
                    icon={<RoomServiceIcon />}
                    label="In-Room Dining"
                          variant="outlined"
                          sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '& .MuiChip-icon': {
                        color: 'primary.main',
                      },
                      px: 1,
                    }}
                  />
                </Stack>
                    </Box>
                  </Grid>
                </Grid>
        </Container>
      </Box>

      {/* All Rooms Listing */}
      <Box
        ref={ref}
        component={motion.div}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={staggerContainer}
        sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              background: theme => `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Our Rooms &amp; Suites
          </Typography>
          <Grid container spacing={4}>
            {rooms.map((room) => (
              <Grid
                item
                key={room.id}
                xs={12}
                sm={6}
                md={3}
                component={motion.div}
                variants={fadeInUp}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                    },
                  }}
                  onClick={() => navigate(`/rooms/${room.id}`)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={room.images[0]}
                      alt={room.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: 'success.main',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      }}
                    >
                      Available
                    </Box>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 700,
                        color: 'primary.main',
                        fontSize: { xs: '1rem', md: '1.15rem' },
                      }}
                    >
                      {room.name}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {room.capacity.adults} Adults
                          {room.capacity.children > 0 && ` + ${room.capacity.children} Child`}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <SquareIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {room.size.squareFeet} sq ft
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                      {room.amenities.slice(0, 3).map((amenity) => (
                        <Chip
                          key={amenity}
                          label={amenity}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1, fontSize: '0.7rem', borderColor: 'rgba(0,0,0,0.12)' }}
                        />
                      ))}
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700, lineHeight: 1 }}>
                          {convertPrice(room.basePrice)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          per night
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          setContactModalRoom(room)
                        }}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          fontWeight: 600,
                          px: 2,
                        }}
                      >
                        Book Now
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  )
}

export default Rooms